package com.example.mystybag.common.ui.components.custom

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusEvent
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.ui.theme.LocalTheme.carosoft
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import androidx.compose.ui.text.font.FontWeight
import com.example.mystybag.core.ui.theme.LocalTheme.p16

/**
 * Поле само за въвеждане на сума. Фиксиран layout – EUR и placeholder/стойност са на едни и същи позиции
 * при фокус и без фокус, без преместване или премигване.
 * [label] – ако е подаден, се показва вътре в полето горе в ляво. Съдържанието (EUR + сума) е в ляво с 8dp.
 */
@Composable
fun AmountInputField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    label: String? = null,
    isError: Boolean = false,
    focusRequester: FocusRequester = remember { FocusRequester() },
    onFocusChanged: ((Boolean) -> Unit)? = null,
    enabled: Boolean = true,
) {
    val theme = AppTheme.current
    val colors = theme.colors
    val typography = theme.typography

    var tfValue by remember(value) { mutableStateOf(TextFieldValue(value, TextRange(value.length))) }
    var isFocused by remember { mutableStateOf(false) }

    LaunchedEffect(value) {
        tfValue = TextFieldValue(value, TextRange(value.length))
    }
    // Синхронизация когато родителят не приема стойността (напр. при отхвърляне заради макс. 999.99)
    SideEffect {
        if (value != tfValue.text) {
            tfValue = TextFieldValue(value, TextRange(value.length))
        }
    }

    val textStyle = typography.bodyLarge.copy(
        fontFamily = carosoft,
        fontWeight = FontWeight.Light,
        fontSize = 16.sp,
        lineHeight = 24.sp,
        letterSpacing = 0.4.sp,
        textAlign = TextAlign.End,
    )

    val borderColor = when {
        !enabled -> colors.outline
        isError -> colors.error
        isFocused -> colors.primary
        else -> colors.outline
    }

    Box(
        modifier = modifier
            .height(p56)
            .fillMaxWidth()
            .background(colors.surface, RoundedCornerShape(p12))
            .border(p1, borderColor, RoundedCornerShape(p12))
            .padding(top = p8, bottom = p8, start = p16, end = p16)
    ) {
        // EUR + сума/placeholder – в дясно; празно място в ляво, после сума, 8dp, EUR
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = p8),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.End
        ) {

            Box(modifier = Modifier.weight(1f)) {
                if (value.isEmpty()) {
                    Texts.BodyMedium(
                        text = placeholder,
                        color = colors.onSurfaceVariant,
                        modifier = Modifier.fillMaxWidth(),
                        textAlign = TextAlign.End,
                    )
                }
                Texts.BodyLarge(
                    text = "EUR",
                    color = colors.onSurface
                )


                BasicTextField(
                    value = tfValue,
                    onValueChange = { newVal ->
                        tfValue = newVal.copy(selection = TextRange(newVal.text.length))
                        onValueChange(newVal.text)
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .focusRequester(focusRequester)
                        .onFocusEvent {
                            isFocused = it.isFocused
                            onFocusChanged?.invoke(it.isFocused)
                        }
                        .background(androidx.compose.ui.graphics.Color.Transparent),
                    textStyle = textStyle.copy(color = colors.onSurface),
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    cursorBrush = SolidColor(colors.primary),
                    readOnly = !enabled,
                )
            }

        }
        // Етикет вътре в полето горе в ляво (Минимум/Максимум)
        label?.let { labelText ->
            Texts.Label(
                text = labelText,
                color = colors.onSurfaceVariant,
                textStyle = typography.labelSmall,
                modifier = Modifier
                    .align(Alignment.TopStart)
            )
        }
    }
}
